import React, { useState } from "react";
import { useSeo } from "../hooks/useSeo";
import {
  Section, SectionHeader, Eyebrow, Reveal, BookButton, StatsBar, VideoCard,
  AudienceCard, TestimonialCard, LogoMarquee, PricingCard, FAQItem, OrangeButton, Card, Link, schedulerLink,
} from "../components/blocks";
import {
  IMAGES, STATS, AUDIENCE, TRUST_CLIENTS,
  VIDEO_TESTIMONIALS, PRICING, FAQS, EXPLAINER_VIDEO_ID,
  CLIENT_VOICES, EXPLAINER_PARAS, SCIENCE, SCIENCE_BODY, CLIENT_STORIES,
  CONTACT_EMAIL, LINKEDIN_URL, LOGO_URL, OG_IMAGES, SAME_AS,
} from "../data/content";
import { useBooking } from "../context/BookingContext";
import { StickyCTA } from "../components/StickyCTA";
import { AwardBadge } from "../components/AwardBadge";
import LatestInsight from "../components/LatestInsight";
import { Play, FileText, ChevronDown, ArrowRight, ArrowDown, Check, Mic } from "lucide-react";
import { OptImg } from "@/components/OptImg";

function Hero() {
  return (
    <section className="bg-ink pt-28 sm:pt-32 pb-24 sm:pb-36 px-6 sm:px-10">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.12)] lg:h-[640px]">
            <OptImg loading="eager" fetchPriority="high" decoding="async" src={IMAGES.heroHome} alt="Maurice Jenkens, intuition and decision-making coach for founders and CEOs" className="w-full aspect-[4/5] lg:aspect-auto lg:h-full object-cover" style={{ objectPosition: "58% 25%" }} />
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <Eyebrow>Founders, Leaders and Entrepreneurs</Eyebrow>
          <h1 className="mt-3 font-display-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.06] text-white">
            How to Make Decisions. <span className="text-ember">Trust Your Gut.</span>
          </h1>
          <p className="mt-9 text-lg text-white max-w-xl leading-relaxed">
            If you are successful, but something still feels off. If you know, but you don't trust. Using a science based method I've developed over 25 years, I help you trust your inner knowing, so you can lead with clarity, purpose and confidence.
          </p>
          <div className="mt-12">
            <BookButton testid="hero-book-btn" utm="home-hero">BOOK A FREE DISCOVERY CALL</BookButton>
            <a href="#lead-magnet" className="block mt-4 text-white/60 hover:text-white text-sm transition-colors" data-testid="hero-assessment-link">
              Test yourself. How much do you trust your gut? Take the free test &rarr;
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Explainer() {
  const [playing, setPlaying] = useState(false);
  const [showText, setShowText] = useState(false);
  return (
    <section className="bg-ink px-6 sm:px-10 py-24 sm:py-32">
      <div className="max-w-6xl mx-auto">
        <Eyebrow>The Method To Trust Your Gut</Eyebrow>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-8">
        <Reveal className="order-2">
          <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.12)] aspect-video" data-testid="explainer-video">
            {playing ? (
              <iframe
                className="w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${EXPLAINER_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                title="What is Decoding Intuition"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              />
            ) : (
              <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0 w-full h-full" data-testid="explainer-video-play" aria-label="Play video">
                <OptImg loading="lazy" src={`https://img.youtube.com/vi/${EXPLAINER_VIDEO_ID}/hqdefault.jpg`} alt="What is Decoding Intuition" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent group-hover:from-ink/60 transition-colors" />
                <div className="absolute bottom-4 left-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 text-white text-sm transition-colors group-hover:bg-white/20">
                    <Play className="w-3.5 h-3.5 fill-white" /> Watch
                  </span>
                </div>
              </button>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.12} className="order-1">
          <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight">What is Decoding Intuition&trade;?</h2>
          <p className="mt-5 text-white text-lg max-w-lg">
            25 years ago I was burned out and had lost my own way. Learning to trust myself again became the method now known as Decoding Intuition&trade;. It is a programme to trust yourself: trusting your intuition to find purpose and trusting your gut enough to act on big decisions. It is also about leaving a meaningful legacy while navigating uncertainty and change.
          </p>
          <div className="mt-7 flex items-center gap-6">
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
              data-testid="method-watch-toggle"
            >
              <Play className="w-4 h-4" /> Watch
            </button>
            <button
              type="button"
              onClick={() => setShowText((v) => !v)}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
              data-testid="method-read-toggle"
              aria-expanded={showText}
            >
              <FileText className="w-4 h-4" /> Prefer to read?
            </button>
          </div>
          {showText && (
            <div className="mt-5 space-y-4 max-w-lg" data-testid="method-read-text">
              {EXPLAINER_PARAS.map((p, i) => (
                <p key={i} className="text-white/80 text-[15px] leading-relaxed">{p}</p>
              ))}
            </div>
          )}
        </Reveal>
        </div>
      </div>
    </section>
  );
}

function ClarityBlock() {
  const { openBooking } = useBooking();
  const includes = ["2-hour 1-1 with Maurice", "Preparation exchanged beforehand", "Follow-up notes with strategic next steps"];
  return (
    <Section bg="bg-ink">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <Eyebrow>Clarity Session</Eyebrow>
          <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight">Have an on-fire issue? Get a Clarity Session.</h2>
          <p className="mt-5 text-white/85 text-lg max-w-lg leading-relaxed">
            For when you have a tough decision or an on-fire problem to solve here and now. It is a 2-hour call and I use a three-step method so we both come prepared to use the time well. You bring questions and on-fire problems, and you leave with a clear, strategic next step you can act on.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <OrangeButton onClick={() => openBooking("Clarity Session")} testid="clarity-block-cta">GET CLARITY NOW</OrangeButton>
            <Link to="/clarity-session-with-maurice" className="text-white/70 hover:text-white text-sm transition-colors" data-testid="clarity-block-link">
              How it works &rarr;
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="bg-surface rounded-2xl p-8 shadow-[0_24px_60px_rgba(0,0,0,0.1)]">
            <div className="rounded-xl overflow-hidden mb-7">
              <OptImg loading="lazy" src={IMAGES.heroHome} alt="Clarity Session with Maurice Jenkens" className="w-full aspect-[16/10] object-cover" style={{ objectPosition: "58% 20%" }} />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-4xl text-white">€599</span>
              <span className="text-white/60 text-sm">one-off</span>
            </div>
            <ul className="mt-6 space-y-3">
              {includes.map((f) => (
                <li key={f} className="flex gap-3 text-white text-[15px]">
                  <Check className="w-5 h-5 text-ember shrink-0 mt-0.5" /><span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

function LeadMagnet() {
  return (
    <section id="lead-magnet" className="bg-cream text-ink px-6 sm:px-10 py-24 sm:py-32">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="font-eyebrow text-ember text-xs mb-5">THE BLACK SWAN ASSESSMENT&trade;</p>
          <h2 className="font-h2 text-3xl sm:text-4xl text-ink leading-tight">How Well Do You Trust Your Gut?</h2>
          <p className="mt-5 text-ink/70 text-lg max-w-md">
            The Black Swan Assessment&trade; gives you your Navigator Score. It shows how ready you are to trust your gut when you must decide amid uncertainty and change AI brings.
          </p>
          <p className="mt-5 text-ink/70 text-lg max-w-md">
            Discover hidden patterns and biases blocking your decision-making. You can get easily started with the free version.
          </p>
          <Link to="/black-swan-assessment" data-testid="leadmagnet-quiz-btn" className="mt-8 inline-flex items-center justify-center font-eyebrow text-xs sm:text-sm text-white bg-ember px-7 py-4 rounded-[8px] tracking-[0.16em] transition-all duration-300 hover:shadow-[0_0_26px_rgba(232,168,124,0.55)] hover:-translate-y-0.5">
            TAKE THE FREE ASSESSMENT
          </Link>
        </Reveal>
        <Reveal delay={0.12}>
          <OptImg loading="lazy" src={IMAGES.quizVisual} alt="Decision style assessment" className="w-full max-w-md mx-auto" />
        </Reveal>
      </div>
    </section>
  );
}

function CTABand() {
  return (
    <section className="bg-surface px-6 sm:px-10 py-20 sm:py-24">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight">Ready to trust your gut?</h2>
          <p className="mt-4 text-white">Book a free discovery call and take the first step.</p>
          <div className="mt-8 flex justify-center">
            <BookButton testid="cta-band-book" utm="home-closing">BOOK A FREE DISCOVERY CALL</BookButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ClientVoices() {
  const [open, setOpen] = useState(false);
  const pairs = CLIENT_VOICES.challenges.map((c, i) => ({ c, r: CLIENT_VOICES.results[i] }));
  const shown = open ? pairs : pairs.slice(0, 3);
  return (
    <Section bg="bg-ink">
      <Eyebrow>What Clients Tell Me About</Eyebrow>
      <p className="mt-3 text-white/55 text-[15px] max-w-2xl">Real client words: where they started, and where they got to.</p>

      <div className="hidden md:grid md:grid-cols-[1fr_3rem_1fr] gap-6 items-center mt-10 mb-3">
        <span className="font-eyebrow text-[11px] tracking-[0.14em] text-white/50">Why They Came</span>
        <span />
        <span className="font-eyebrow text-[11px] tracking-[0.14em]" style={{ color: "#E8A87C" }}>What They Got</span>
      </div>

      <div className="space-y-4 mt-6 md:mt-0">
        {shown.map((p, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div className="grid md:grid-cols-[1fr_3rem_1fr] gap-3 md:gap-6 items-stretch" data-testid={`voice-pair-${i}`}>
              <div className="rounded-xl p-6 flex items-center" style={{ backgroundColor: "#0E1621" }}>
                <p className="text-[15px] text-white/75 leading-relaxed">{p.c}</p>
              </div>
              <div className="flex items-center justify-center py-1 md:py-0">
                <ArrowRight className="hidden md:block w-6 h-6" style={{ color: "#CC4100" }} />
                <ArrowDown className="md:hidden w-5 h-5" style={{ color: "#CC4100" }} />
              </div>
              <div className="rounded-xl p-6 flex items-center border-l-2" style={{ backgroundColor: "#101A26", borderColor: "#CC4100" }}>
                <p className="text-[15px] text-white leading-relaxed">{p.r}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {pairs.length > 3 && (
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="voice-expand"
          className="mt-6 inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition-colors"
          aria-expanded={open}
        >
          {open ? "Show less" : "Show more"}
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
      )}
    </Section>
  );
}

function ScienceCredibility() {
  return (
    <Section>
      <SectionHeader eyebrow="Backed by Research" title="The science behind trusting your gut." sub={SCIENCE_BODY} />
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {SCIENCE.map((s, i) => (
          <Reveal key={s.name} delay={i * 0.1}>
            <VideoCard name={s.name} note={s.note} thumb={s.thumb} epNum={s.n} shareSlug={s.slug} youtubeId={s.youtube} audioUrl={s.audio} testid={`home-science-${i}`} />
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

function ClarityOptions() {
  return null;
}

function ClientStories() {
  return (
    <Section bg="bg-ink">
      <Eyebrow>What Clients Say</Eyebrow>
      <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight max-w-3xl">Real results from founders and leaders.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mt-12">
        {CLIENT_STORIES.map((s, i) => (
          <Reveal key={s.name} delay={i * 0.08}>
            <div className="h-full flex flex-col" data-testid={`client-story-${i}`}>
              <OptImg sizes="96px" loading="lazy" src={s.photo} alt={`${s.name}, ${s.role}`} className="w-16 h-16 rounded-full object-cover" />
              <p className="font-name text-base text-white leading-tight mt-4">{s.name}</p>
              <p className="text-[13px] text-white/55">{s.title}</p>
              <p className="mt-5 text-[15px] text-white leading-relaxed lg:min-h-[5.5rem]">{s.transform}</p>
              <p className="mt-5 text-[15px] text-white font-name leading-snug">
                <span className="text-ember mr-1">&ldquo;</span>{s.punch}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

const AUDIENCE_VISUAL = [
  {
    role: "Founder / Owner",
    eyebrow: "FULFILMENT",
    note: "Successful but unfulfilled. Seeking what's next.",
    quote: "Financially safe enough, but inner emptiness. Something is missing.",
    img: "https://static.prod-images.emergentagent.com/jobs/1304e1bd-d610-4f8e-8ace-e55a7b2f3709/images/9be5dc90dc1b588b3a027e0c2fef8d0eaeb62588b51471223c672b8d674206c8.png",
  },
  {
    role: "CEO / Executive",
    eyebrow: "TRUST",
    note: "Level up career. Big decisions, AI uncertainty.",
    quote: "I need to trust my gut better on the big decisions, AI, recruiting, investing, who to partner with.",
    img: "https://static.prod-images.emergentagent.com/jobs/1304e1bd-d610-4f8e-8ace-e55a7b2f3709/images/e8c92a42d1cbbb77cdcc0e5c30e19915ed59d5bac12ebe8fbb8ffa6c06fa5edc.png",
  },
  {
    role: "Solo Entrepreneur",
    eyebrow: "FREEDOM",
    note: "Building from passion. Seek direction in business.",
    quote: "I want to build something of my own. I just need to know what, and how.",
    img: "https://static.prod-images.emergentagent.com/jobs/1304e1bd-d610-4f8e-8ace-e55a7b2f3709/images/7b9c18814ec3e73b59537fad7434d777f20930d0e9bfa7d152ac3e8e2fd20976.png",
  },
  {
    role: "Exited Founder",
    eyebrow: "IDENTITY",
    note: "What is next in life? Leave a legacy.",
    quote: "Loss of role, title and daily routine. Who am I if not the founder? What's next for me?",
    img: "https://static.prod-images.emergentagent.com/jobs/1304e1bd-d610-4f8e-8ace-e55a7b2f3709/images/cfd0f5815eb905d840b9aef41ee1eda28de7a0ee3a24de5752d6e76d3f126e53.png",
  },
];

const WHO_LABEL_COLOR = "#E8C9A0";

function FlipCard({ a }) {
  const [flipped, setFlipped] = useState(false);
  const label = a.eyebrow.toLowerCase();
  return (
    <div
      className="group h-full [perspective:1400px] cursor-pointer"
      onClick={() => setFlipped((f) => !f)}
      data-testid={`who-flip-${label}`}
    >
      <div
        className={`relative h-full transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] ${flipped ? "[transform:rotateY(180deg)]" : ""}`}
      >
        {/* Front */}
        <div className="bg-surface rounded-2xl overflow-hidden h-full flex flex-col [backface-visibility:hidden]">
          <div className="relative">
            <span
              className="absolute top-3 left-4 z-10 font-eyebrow text-[10px] tracking-[0.18em]"
              style={{ color: WHO_LABEL_COLOR, opacity: 0.85 }}
            >
              {a.eyebrow}
            </span>
            <OptImg loading="lazy" src={a.img} alt={`${a.role} — coaching to trust your gut and find purpose`} className="w-full aspect-square object-cover" />
            <div
              className="absolute inset-0 pointer-events-none mix-blend-screen"
              style={{ background: "radial-gradient(circle at 50% 46%, rgba(204,65,0,0.28), transparent 58%)" }}
            />
          </div>
          <div className="p-7 text-center flex flex-col flex-1">
            <h3 className="font-display text-xl text-white leading-tight">{a.role}</h3>
            <p className="mt-3 text-white/65 text-[15px] leading-relaxed">{a.note}</p>
          </div>
        </div>
        {/* Back */}
        <div className="absolute inset-0 bg-surface rounded-2xl overflow-hidden flex flex-col p-7 [backface-visibility:hidden] [transform:rotateY(180deg)]">
          <span
            className="absolute top-3 left-4 z-10 font-eyebrow text-[10px] tracking-[0.18em]"
            style={{ color: WHO_LABEL_COLOR, opacity: 0.85 }}
          >
            {a.eyebrow}
          </span>
          <div className="flex-1 flex items-center justify-center text-center px-1">
            <p className="italic text-white/85 text-[15px] leading-relaxed">{a.quote}</p>
          </div>
          <div className="flex justify-center">
            <a
              href={schedulerLink("home-who-" + label)}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`who-flip-cta-${label}`}
              className="inline-flex items-center justify-center font-eyebrow text-[10px] tracking-[0.14em] text-embersoft border border-ember/40 hover:border-ember hover:bg-ember hover:text-white px-4 py-2 rounded-full transition-all duration-300"
            >
              Book a discovery call
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhoVisual() {
  return (
    <Section bg="bg-ink">
      <Eyebrow>Who This Is For</Eyebrow>
      <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight max-w-3xl">Founders, CEOs and entrepreneurs.</h2>
      <p className="mt-5 text-white/70 text-lg max-w-2xl">
        Whether you are seeking purpose, having to make decisions under uncertainty, building freedom in your business, or searching for fulfilment after an exit, this is a place to deepen your self-trust and act on it.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
        {AUDIENCE_VISUAL.map((a, i) => (
          <Reveal key={a.role} delay={i * 0.08} className="h-full">
            <FlipCard a={a} />
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

const OUTCOMES = [
  "Trust your gut and make clearer decisions under uncertainty",
  "Find purpose and direction in life and business",
  "Stop second-guessing yourself and act with confidence",
  "Lead with calm and clarity amid change and AI disruption",
  "Reconnect with fulfilment, freedom and a legacy worth leaving",
  "Turn intuition from a vague feeling into a skill you can trust and use",
];

function Outcomes() {
  return (
    <Section>
      <Eyebrow>What You'll Learn</Eyebrow>
      <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight max-w-3xl">Outcomes you can expect.</h2>
      <p className="mt-5 text-white/70 text-lg max-w-2xl">
        By the end of working together, you will have trained your intuition into a practical, everyday skill. Here is what that looks like.
      </p>
      <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4 mt-10 max-w-4xl">
        {OUTCOMES.map((o, i) => (
          <Reveal key={o} delay={i * 0.06}>
            <li className="flex gap-3 text-white text-[15px] leading-relaxed" data-testid={`outcome-${i}`}>
              <Check className="w-5 h-5 text-ember shrink-0 mt-0.5" />
              <span>{o}</span>
            </li>
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}

function WhatIs() {
  return (
    <Section>
      <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-20 items-center">
        <Reveal className="max-w-3xl">
          <Eyebrow>What is The Intuitive Leader&trade;?</Eyebrow>
          <h2 className="font-h2 text-3xl sm:text-4xl text-white leading-tight">Trust yourself.</h2>
          <p className="mt-6 text-white/70 text-lg leading-relaxed">
            In short, The Intuitive Leader&trade; is the work of Maurice Jenkens, who helps founders, CEOs and leaders trust their intuition and make better decisions under uncertainty. Using Decoding Intuition&trade;, a science based method developed over 25 years, he turns inner knowing into clarity and confident action, and helps clients find purpose, freedom and fulfilment beyond business success.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="flex lg:justify-center shrink-0">
          <AwardBadge caption="Twice nominated for Stora Talarpriset, the Swedish national speaker award." className="max-w-[240px]" />
        </Reveal>
      </div>
    </Section>
  );
}

const HOME_FEATURED_ON = [
  "The Intuitive-Psychology Association Podcast",
  "The Pivot",
  "Mission Facilitators International Podcast",
  "The Exit Engine Podcast",
  "Eudaimonia Coaching",
  "Asia Pacific Alliance of Coaches (APAC)",
];

function FeaturedOn() {
  return (
    <div className="bg-ink pb-20 sm:pb-24" data-testid="home-featured-on">
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <p className="font-eyebrow text-white text-[11px] tracking-[0.14em] mb-7">Featured on</p>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          {HOME_FEATURED_ON.map((name) => (
            <span key={name} className="flex items-center gap-2.5 text-white/45 text-sm sm:text-base font-name">
              <Mic className="w-4 h-4 text-white/40 shrink-0" strokeWidth={2} />
              {name}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://intuitiveleader.net";
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    speakable: { "@type": "SpeakableSpecification", cssSelector: [".faq-q", ".faq-a"] },
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Maurice Jenkens",
    jobTitle: "Leadership, Sales and Intuition Coach",
    description: "Creator of the science-based Decoding Intuition™ method, helping founders, CEOs and entrepreneurs trust their gut, make better decisions and find purpose.",
    url: origin,
    image: LOGO_URL,
    email: CONTACT_EMAIL,
    sameAs: SAME_AS,
    knowsAbout: ["intuitive leadership", "trusting your gut", "decision-making under uncertainty", "psychological safety", "high-performance teams", "self-trust", "reading the room", "B2B sales", "the science of intuition", "finding your purpose", "executive coaching", "sales coaching", "leadership development"],
    worksFor: { "@type": "Organization", name: "The Intuitive Leader", url: origin },
  };
  const decodingServiceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Decoding Intuition™",
    serviceType: "Intuition and decision-making coaching",
    description: "A coaching programme to trust your intuition, make better decisions under uncertainty and find purpose.",
    provider: { "@type": "Person", name: "Maurice Jenkens" },
    url: origin,
    offers: PRICING.map((p) => ({
      "@type": "Offer",
      name: p.name,
      price: (p.price || "").replace(/[^0-9]/g, ""),
      priceCurrency: "EUR",
    })),
  };
  const explainerVideoJsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "What is Decoding Intuition™?",
    description: "Maurice Jenkens explains Decoding Intuition™, the science-based method he developed over 25 years to help founders, CEOs and leaders trust their gut and make better decisions.",
    thumbnailUrl: `https://img.youtube.com/vi/${EXPLAINER_VIDEO_ID}/hqdefault.jpg`,
    uploadDate: "2025-08-15T01:09:17-07:00",
    contentUrl: `https://www.youtube.com/watch?v=${EXPLAINER_VIDEO_ID}`,
    embedUrl: `https://www.youtube.com/embed/${EXPLAINER_VIDEO_ID}`,
  };
  const videoTestimonialsJsonLd = VIDEO_TESTIMONIALS.map((v) => ({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: `${v.name} on working with Maurice Jenkens`,
    description: `${v.name}, ${v.title}: "${v.pull}"`,
    thumbnailUrl: `https://img.youtube.com/vi/${v.youtube}/hqdefault.jpg`,
    uploadDate: v.date,
    contentUrl: `https://www.youtube.com/watch?v=${v.youtube}`,
    embedUrl: `https://www.youtube.com/embed/${v.youtube}`,
  }));
  useSeo(
    "How to Make Decisions. Trust Your Gut. | Maurice Jenkens",
    "Learn to trust your gut and make better decisions under uncertainty. Maurice Jenkens helps founders & CEOs find purpose with the science-based Decoding Intuition™ method. Book a free discovery call.",
    { image: OG_IMAGES.home, jsonLd: [personJsonLd, decodingServiceJsonLd, faqJsonLd, explainerVideoJsonLd, ...videoTestimonialsJsonLd], keywords: "how to make decisions, trust your gut, decision making, self trust, intuitive leadership, founder coaching, how to make decisions under uncertainty, how to find your purpose, how to trust yourself again, how to find fulfillment as a founder, how to find fulfilment as a founder, how to find freedom in business, how to find purpose after exit, how to know what you want in life, how to stop second guessing yourself, can intuition be trained, executive coaching, leadership coaching" }
  );

  return (
    <>
      <StickyCTA label="BOOK A FREE DISCOVERY CALL" utm="home" />
      {/* 1. Hero */}
      <Hero />

      {/* 2. Numbers bar + trust strip (one credibility band under hero) */}
      <StatsBar stats={STATS} />
      <div className="bg-ink pb-20 sm:pb-24 -mt-4 sm:-mt-6">
        <p className="text-center font-eyebrow text-[11px] tracking-[0.14em] text-white/35 mb-7">Trusted by teams at</p>
        <LogoMarquee logos={TRUST_CLIENTS} />
      </div>

      {/* What is The Intuitive Leader */}
      <WhatIs />

      {/* Who this is for */}
      <WhoVisual />

      {/* What Clients Say — face + transformation */}
      <ClientStories />

      {/* What is Decoding Intuition (split video) */}
      <Explainer />

      {/* What you'll learn / outcomes */}
      <Outcomes />

      {/* Science */}
      <ScienceCredibility />

      {/* Video testimonials (warm proof before the ask) */}
      <Section bg="bg-ink">
        <SectionHeader eyebrow="Hear It From Them" title="Client video testimonials." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {VIDEO_TESTIMONIALS.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.08}>
              <VideoCard name={v.name} note={v.title} thumb={v.thumb} youtubeId={v.youtube} pull={v.pull} testid={`video-test-${i}`} />
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow className="text-center">The Program</Eyebrow>
          <h2 className="font-h2 text-3xl sm:text-4xl text-white mt-2">Decoding Intuition&trade;: Coaching to Trust Your Gut</h2>
          <p className="mt-3 text-white/80 text-[15px]">How ready are you to trust yourself and act? Choose your pace.</p>
        </Reveal>
        <div className="grid lg:grid-cols-3 gap-6 mt-14 items-stretch">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}><PricingCard plan={p} /></Reveal>
          ))}
        </div>
      </Section>

      {/* Clarity Session block */}
      <ClarityBlock />

      {/* Free Assessment */}
      <LeadMagnet />

      {/* FAQ */}
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Trust Your Gut: Frequently Asked Questions" center />
        <div className="max-w-3xl mx-auto mt-12 space-y-4">
          {FAQS.map((f, i) => <Reveal key={i} delay={i * 0.05}><FAQItem item={f} index={i} /></Reveal>)}
        </div>
      </Section>

      {/* Latest insight */}
      <LatestInsight />

      {/* 13. Closing CTA */}
      <CTABand />
    </>
  );
}
